import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { EmployeeHeader } from "../components/EmployeeHeader";
import { Skeleton } from "../components/ui/skeleton";
import { ClipboardCheck, Calendar, ArrowRight, Clock } from "lucide-react";

const PRIORITY_STYLE = {
  urgent: { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", pin: "bg-red-500", label: "Urgent" },
  high: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", pin: "bg-amber-500", label: "High" },
  medium: { bg: "bg-sky-50", border: "border-sky-300", text: "text-sky-700", pin: "bg-sky-500", label: "Medium" },
  low: { bg: "bg-slate-50", border: "border-slate-300", text: "text-slate-600", pin: "bg-slate-400", label: "Low" },
};

function fmtDue(dl) {
  if (!dl) return null;
  try {
    return new Date(`${dl}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  } catch {
    return dl;
  }
}

function StickyNote({ task, tilt }) {
  const p = PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.medium;
  const shownItems = (task.items || []).slice(0, 2);
  const moreItems = (task.item_count || 0) > shownItems.length;
  return (
    <Link
      to={`/my-tasks/${task.id}`}
      data-testid={`sticky-task-${task.id}`}
      className={`relative block rounded-2xl border-2 ${p.border} ${p.bg} p-3.5 pt-4 shadow-md hover:shadow-lg transition active:scale-[0.97] ${tilt}`}
    >
      <span className={`absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full ${p.pin} shadow`} aria-hidden />
      <div className={`text-[10px] font-bold uppercase tracking-wider ${p.text} mb-1.5`} data-testid={`sticky-priority-${task.id}`}>
        {p.label}
      </div>
      <div className="font-bold text-slate-900 text-sm leading-snug truncate mb-1" data-testid={`sticky-client-${task.id}`}>
        {task.client_name || task.title}
      </div>
      {shownItems.length > 0 && (
        <div className="text-xs text-slate-600 leading-snug mb-1.5 line-clamp-2" data-testid={`sticky-items-${task.id}`}>
          {shownItems.join(", ")}{moreItems ? "…" : ""}
        </div>
      )}
      {task.deadline && (
        <div className="text-[11px] font-semibold text-slate-500 flex items-center gap-1">
          <Calendar className="w-3 h-3" /> Due {fmtDue(task.deadline)}
        </div>
      )}
      {(task.expected_delivery_date || task.expected_delivery_time) && (
        <div
          data-testid={`sticky-delivery-${task.id}`}
          className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded"
        >
          <Clock className="w-3 h-3" /> {[task.expected_delivery_date, task.expected_delivery_time].filter(Boolean).join(" · ")}
        </div>
      )}
    </Link>
  );
}

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/me/dashboard").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const pending = data?.pending_tasks || [];

  return (
    <>
      <EmployeeHeader subtitle={data?.owner?.business_name ? `for ${data.owner.business_name}` : (user?.name || "")} />
      <div className="px-4 pt-5 pb-28 space-y-4">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-sm font-medium text-indigo-700 uppercase tracking-wider">Hello {user?.name?.split(" ")[0]}</p>
            <h2 className="text-2xl font-bold text-slate-900">Today's Work</h2>
          </div>
          <Link to="/my-tasks" data-testid="emp-home-view-history" className="text-sm font-semibold text-indigo-700 flex items-center gap-1 shrink-0">
            History <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div>
        ) : pending.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-200" data-testid="emp-home-empty">
            <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto grid place-items-center mb-3">
              <ClipboardCheck className="w-7 h-7 text-emerald-500" strokeWidth={2.5} />
            </div>
            <p className="text-slate-700 font-semibold mb-1">All caught up!</p>
            <p className="text-sm text-slate-500">No pending tasks — your boss will assign work soon</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3" data-testid="emp-sticky-grid">
            {pending.map((t, i) => (
              <StickyNote key={t.id} task={t} tilt={i % 2 === 0 ? "-rotate-1" : "rotate-1"} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
